import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Grid,
    Paper,
    TextField,
    Button,
    CircularProgress,
    Alert,
    Modal,
    Box,
    Card,
    CardContent,
    Divider,
    InputAdornment,
    IconButton,
    useTheme,
    Snackbar
} from "@mui/material";
import {
    Lock,
    Person,
    Visibility,
    VisibilityOff,
    AccountCircle,
    Edit,
    Save
} from "@mui/icons-material";
import { useAuth } from '../../context/AuthContext';

const drawerWidth = 300;

// Modal styles
const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: { xs: '90%', sm: 450 },
    maxWidth: 500,
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
    borderRadius: 3,
    maxHeight: '90vh',
    overflow: 'auto'
};

const AdminProfile = () => {
    const theme = useTheme();
    const { currentUser, changePassword, changeUsername } = useAuth();

    // State for form data
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        username: ''
    });

    // State for modals
    const [openPasswordModal, setOpenPasswordModal] = useState(false);
    const [openUsernameModal, setOpenUsernameModal] = useState(false);

    // State for password change
    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: ''
    });
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);

    // State for username change
    const [usernameData, setUsernameData] = useState({
        password: '',
        newUsername: ''
    });
    const [showUsernamePassword, setShowUsernamePassword] = useState(false);

    // State for feedback
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [usernameError, setUsernameError] = useState('');
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');

    // Initialize form data
    useEffect(() => {
        if (currentUser) {
            setFormData({
                firstName: currentUser.name?.firstName || '',
                lastName: currentUser.name?.lastName || '',
                username: currentUser.username || ''
            });
        }
    }, [currentUser]);

    // Handle form field changes for profile
    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    // Handle password field changes
    const handlePasswordChange = (e) => {
        setPasswordData({
            ...passwordData,
            [e.target.name]: e.target.value
        });
    };

    // Handle username field changes
    const handleUsernameChange = (e) => {
        setUsernameData({
            ...usernameData,
            [e.target.name]: e.target.value
        });
    };

    // Handle form submission for profile update
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccess('');

        if (!formData.firstName || !formData.lastName) {
            setError('All fields are required.');
            setLoading(false);
            return;
        }

        try {
            // API call to update profile would go here
            setSuccess('Profile updated successfully!');
            setSnackbarMessage('Profile updated successfully!');
            setSnackbarOpen(true);
        } catch (err) {
            setError('Failed to update profile. Please try again.');
        }

        setLoading(false);
    };

    // Handle password change submission
    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setPasswordError('');
        setSuccess('');

        if (!passwordData.currentPassword || !passwordData.newPassword) {
            setPasswordError('Both current and new password fields are required.');
            setLoading(false);
            return;
        }

        if (passwordData.newPassword.length < 6) {
            setPasswordError('New password must be at least 6 characters long.');
            setLoading(false);
            return;
        }

        try {
            const response = await changePassword(passwordData.currentPassword, passwordData.newPassword);
            if (response.success) {
                setSnackbarMessage(response.message || 'Password Changed Successfully');
                setSnackbarOpen(true);
                setPasswordData({ currentPassword: '', newPassword: '' });
                setOpenPasswordModal(false);
            } else {
                setPasswordError(response.error || 'Failed to update password.');
            }
        } catch (err) {
            const errorMessage = err.response?.data?.error || err.message || 'Failed to update password. Please try again.';
            setPasswordError(errorMessage);
        }

        setLoading(false);
    };

    // Handle username change submission
    const handleUsernameSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setUsernameError('');
        setSuccess('');

        if (!usernameData.password || !usernameData.newUsername) {
            setUsernameError('Both password and new username fields are required.');
            setLoading(false);
            return;
        }

        if (usernameData.newUsername.length < 3) {
            setUsernameError('Username must be at least 3 characters long.');
            setLoading(false);
            return;
        }

        try {
            const response = await changeUsername(usernameData.password, usernameData.newUsername);
            if (response.success) {
                setSnackbarMessage(response.message || 'Username changed successfully');
                setSnackbarOpen(true);
                setFormData(prev => ({ ...prev, username: response.username }));
                setUsernameData({ password: '', newUsername: '' });
                setOpenUsernameModal(false);
            } else {
                setUsernameError(response.error || 'Failed to update username.');
            }
        } catch (err) {
            const errorMessage = err.response?.data?.error || err.message || 'Failed to update username. Please try again.';
            setUsernameError(errorMessage);
        }

        setLoading(false);
    };

    return (
        <Container
            maxWidth="xl"
            sx={{
                py: 4,
                px: { xs: 2, sm: 3 },
                backgroundColor: theme.palette.background.default,
                minHeight: "100vh",
            }}
        >
            {/* Page Header */}
            <Box sx={{ mb: 4 }}>
                <Typography
                    variant="h4"
                    component="h1"
                    sx={{
                        fontWeight: 700,
                        color: theme.palette.primary.main,
                        mb: 1,
                        p: 0
                    }}
                

                >
                    Admin Profile
                </Typography>
                <Typography variant="body2" color="text.secondary" p={0} m={0}>
                    Manage your account settings and preferences
                </Typography>
            </Box>

            {/* Feedback Messages */}
            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}

            <Grid container spacing={3}>
                {/* Profile Information Card */}
                <Grid item xs={12} md={8}>
                    <Card elevation={2}>
                        <CardContent sx={{ p: 4 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <AccountCircle sx={{ fontSize: 40, color: theme.palette.primary.main, mr: 2 }} />
                                <Typography variant="h6" fontWeight={600}>
                                    Profile Information
                                </Typography>
                            </Box>
                            <Divider sx={{ mb: 3 }} />

                            <form onSubmit={handleSubmit}>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="First Name"
                                            name="firstName"
                                            value={formData.firstName}
                                            onChange={handleChange}
                                            required
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Person color="action" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Last Name"
                                            name="lastName"
                                            value={formData.lastName}
                                            onChange={handleChange}
                                            required
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Person color="action" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Username"
                                            name="username"
                                            value={formData.username}
                                            disabled
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Person color="action" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            helperText="Username can be changed separately"
                                            FormHelperTextProps={{
                                                sx: { m: 0, p: 0, mt: 0.5 }
                                            }}
                                        />
                                    </Grid>
                                </Grid>

                                <Button
                                    type="submit"
                                    variant="contained"
                                    startIcon={loading ? <CircularProgress size={20} /> : <Save />}
                                    disabled={loading}
                                    sx={{
                                        mt: 3,
                                        px: 4,
                                        py: 1.5,
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        fontWeight: 600
                                    }}
                                >
                                    {loading ? 'Updating...' : 'Update Profile'}
                                </Button>
                            </form>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Security Settings Card */}
                <Grid item xs={12} md={4}>
                    <Card elevation={2} sx={{ mb: 3 }}>
                        <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Lock sx={{ fontSize: 32, color: theme.palette.secondary.main, mr: 1.5 }} />
                                <Typography variant="h6" fontWeight={600}>
                                    Security
                                </Typography>
                            </Box>
                            <Divider sx={{ mb: 3 }} />

                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                Keep your account secure by regularly updating your credentials
                            </Typography>

                            <Button
                                variant="outlined"
                                fullWidth
                                startIcon={<Lock />}
                                onClick={() => setOpenPasswordModal(true)}
                                sx={{
                                    mb: 2,
                                    py: 1.2,
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontWeight: 600
                                }}
                            >
                                Change Password
                            </Button>

                            <Button
                                variant="outlined"
                                fullWidth
                                startIcon={<Edit />}
                                onClick={() => setOpenUsernameModal(true)}
                                sx={{
                                    py: 1.2,
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    borderColor: theme.palette.info.main,
                                    color: theme.palette.info.main,
                                    '&:hover': {
                                        borderColor: theme.palette.info.dark,
                                        backgroundColor: 'rgba(0, 123, 255, 0.04)'
                                    }
                                }}
                            >
                                Change Username
                            </Button>
                        </CardContent>
                    </Card>

                    {/* Account Info Card */}
                    <Card elevation={2}>
                        <CardContent sx={{ p: 3 }}>
                            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                                Account Info
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            <Box>
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="caption" color="text.secondary" p={0} m={0}>
                                        EMAIL
                                    </Typography>
                                    <Typography variant="body2" fontWeight={500} p={0} m={0}>
                                        {currentUser?.email || 'N/A'}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary" p={0} m={0}>
                                        ROLE
                                    </Typography>
                                    <Typography variant="body2" fontWeight={500} p={0} m={0}>
                                        Administrator
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Snackbar for success messages */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={4000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert onClose={() => setSnackbarOpen(false)} severity="success" sx={{ width: '100%' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>

            {/* Password Change Modal */}
            <Modal
                open={openPasswordModal}
                onClose={() => {
                    setOpenPasswordModal(false);
                    setPasswordError('');
                    setPasswordData({ currentPassword: '', newPassword: '' });
                }}
            >
                <Box sx={modalStyle}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        <Lock sx={{ fontSize: 32, color: theme.palette.primary.main, mr: 1.5 }} />
                        <Typography variant="h6" fontWeight={600}>
                            Change Password
                        </Typography>
                    </Box>
                    <Divider sx={{ mb: 3 }} />

                    <form onSubmit={handlePasswordSubmit}>
                        <TextField
                            fullWidth
                            label="Current Password"
                            name="currentPassword"
                            type={showCurrentPassword ? "text" : "password"}
                            value={passwordData.currentPassword}
                            onChange={handlePasswordChange}
                            required
                            sx={{ mb: 2.5 }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                            edge="end"
                                        >
                                            {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <TextField
                            fullWidth
                            label="New Password"
                            name="newPassword"
                            type={showNewPassword ? "text" : "password"}
                            value={passwordData.newPassword}
                            onChange={handlePasswordChange}
                            required
                            sx={{ mb: 2 }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowNewPassword(!showNewPassword)}
                                            edge="end"
                                        >
                                            {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            helperText="Password must be at least 6 characters long"
                            FormHelperTextProps={{
                                sx: { m: 0, p: 0, mt: 0.5 }
                            }}
                        />
                        
                        {passwordError && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {passwordError}
                            </Alert>
                        )}

                        <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                            <Button
                                onClick={() => {
                                    setOpenPasswordModal(false);
                                    setPasswordError('');
                                    setPasswordData({ currentPassword: '', newPassword: '' });
                                }}
                                variant="outlined"
                                fullWidth
                                sx={{ textTransform: 'none', fontWeight: 600 }}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                fullWidth
                                disabled={loading}
                                sx={{ textTransform: 'none', fontWeight: 600 }}
                            >
                                {loading ? <CircularProgress size={24} /> : 'Update Password'}
                            </Button>
                        </Box>
                    </form>
                </Box>
            </Modal>

            {/* Username Change Modal */}
            <Modal
                open={openUsernameModal}
                onClose={() => {
                    setOpenUsernameModal(false);
                    setUsernameError('');
                    setUsernameData({ password: '', newUsername: '' });
                }}
            >
                <Box sx={modalStyle}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        <Edit sx={{ fontSize: 32, color: theme.palette.info.main, mr: 1.5 }} />
                        <Typography variant="h6" fontWeight={600}>
                            Change Username
                        </Typography>
                    </Box>
                    <Divider sx={{ mb: 3 }} />

                    <form onSubmit={handleUsernameSubmit}>
                        <TextField
                            fullWidth
                            label="New Username"
                            name="newUsername"
                            value={usernameData.newUsername}
                            onChange={handleUsernameChange}
                            required
                            sx={{ mb: 2.5 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Person color="action" />
                                    </InputAdornment>
                                ),
                            }}
                            helperText="Username must be at least 3 characters long"
                            FormHelperTextProps={{
                                sx: { m: 0, p: 0, mt: 0.5 }
                            }}
                        />
                        <TextField
                            fullWidth
                            label="Password"
                            name="password"
                            type={showUsernamePassword ? "text" : "password"}
                            value={usernameData.password}
                            onChange={handleUsernameChange}
                            required
                            sx={{ mb: 2 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Lock color="action" />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowUsernamePassword(!showUsernamePassword)}
                                            edge="end"
                                        >
                                            {showUsernamePassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            helperText="Enter your current password to confirm"
                            FormHelperTextProps={{
                                sx: { m: 0, p: 0, mt: 0.5 }
                            }}
                        />
                        
                        {usernameError && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {usernameError}
                            </Alert>
                        )}

                        <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                            <Button
                                onClick={() => {
                                    setOpenUsernameModal(false);
                                    setUsernameError('');
                                    setUsernameData({ password: '', newUsername: '' });
                                }}
                                variant="outlined"
                                fullWidth
                                sx={{ textTransform: 'none', fontWeight: 600 }}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                fullWidth
                                disabled={loading}
                                sx={{ textTransform: 'none', fontWeight: 600 }}
                            >
                                {loading ? <CircularProgress size={24} /> : 'Update Username'}
                            </Button>
                        </Box>
                    </form>
                </Box>
            </Modal>
        </Container>
    );
};

export default AdminProfile;
